/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * List.tsx
 */

import React from 'react';
import ListAdapter, {AdapterListener, ListWatcher} from "./ListAdapter";
import ListRow from "./ListRow";
import {Column} from "./Column";
import {Order} from "../model/pagination/Sort";
import HiloReactComponent from "../view/layout/HiloReactComponent";
import {Entity} from "../model/pagination/Entity";
import Spinner from "../view/element/Spinner";
import {APIPagination} from "../pagination/APIPagination";
import {IonIcon} from "../view/element/IonIcon";


interface Props<E extends Entity, L extends AdapterListener<E>, P extends APIPagination> {
    adapter: ListAdapter<E, L, P>;
}

interface State {
}

export default class ListContent<E extends Entity, L extends AdapterListener<E>, P extends APIPagination> extends HiloReactComponent<Props<E, L, P>, State> implements ListWatcher {

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this.props.adapter.addListWatcher(this);
    }

    /**
     * componentWillUnmount
     */
    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this.props.adapter.removeListWatcher(this);
    }

    /**
     * onUpdate
     */
    public onUpdate = (): void => {
        this.forceUpdate();
    };

    /**
     * _sortIcon
     * @private
     */
    private _sortIcon = (props: { column: Column<E> }): JSX.Element => {
        const {adapter} = this.props;

        const sort = adapter.getSort();
        if (!sort || (sort && sort.by !== props.column.propertyName)) {
            return <></>;
        }

        return <span className="icon"><IonIcon
            name={`arrow-${sort.order === Order.ASC ? "up" : "down"}-circle`}/></span>
    };

    /**
     * render
     */
    public render(): JSX.Element {
        const {adapter} = this.props;
        let key = 0;

        if (adapter.loading) {
            return <Spinner large={true}/>
        }

        return <table>
            <thead>
            <tr>
                {adapter.getColumns().map(column => {
                    return <th
                        className={"table-header"}
                        key={column.displayKey}
                        onClick={() => adapter.setSort(column.propertyName)}>
                        {column.displayKey}
                        <this._sortIcon column={column}/>
                    </th>
                })}
            </tr>
            </thead>
            <tbody>
            {
                adapter.getCount() > 0 ?
                    adapter.getItems().map(item => {
                        return <tr key={key++} className={"list-element"}
                                   onClick={() => adapter.itemClicked(item)}>
                            <ListRow entity={item} adapter={adapter}/>
                        </tr>
                    })
                    :
                    <tr>
                        <td><p className={"list-item"}>{adapter.getEmptyListPlaceholder()}</p></td>
                    </tr>
            }
            </tbody>
        </table>;
    }
}